import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Grid, Link, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useOrganization, useUpdateOrganization } from '../hooks/api/useOrganizations';
import { useOrganizationMetrics } from '../hooks/api/useMetrics';
import { usePlans } from '../hooks/api/usePlans';
import { useCreateSubscription, useUpdateSubscription } from '../hooks/api/useSubscriptions';
import { PlatformMetricsView } from '../components/metrics/PlatformMetricsView';
import { OrganizationDetailsForm } from '../components/OrganizationDetailsForm';
import { OrganizationEntitlementsPanel } from '../components/OrganizationEntitlementsPanel';
import { OrganizationSubscriptionPanel } from '../components/OrganizationSubscriptionPanel';

export function OrganizationDetailPage() {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const { data: org, isLoading, error, refetch } = useOrganization(uuid);
  const { data: orgMetrics, isLoading: metricsLoading, error: metricsError } = useOrganizationMetrics(uuid);
  const { data: plans } = usePlans();
  const updateOrganization = useUpdateOrganization(uuid);
  const createSubscription = useCreateSubscription();

  const subscriptionUuid = org?.subscription?.uuid;
  const updateSubscription = useUpdateSubscription(subscriptionUuid ?? '');

  const [orgForm, setOrgForm] = useState({
    name: '',
    nickname: '',
    billingEmail: '',
    taxId: '',
  });

  const [subscriptionForm, setSubscriptionForm] = useState({
    planUuid: '',
    status: 'active',
    trialDays: 14,
  });

  useEffect(() => {
    if (!org) return;
    setOrgForm({
      name: org.name ?? '',
      nickname: org.nickname ?? '',
      billingEmail: org.billingEmail ?? '',
      taxId: org.taxId ?? '',
    });
    if (org.subscription) {
      setSubscriptionForm({
        planUuid: org.subscription.plan?.uuid ?? '',
        status: org.subscription.status,
        trialDays: 14,
      });
    }
  }, [org]);

  const activePlans = Array.isArray(plans) ? plans.filter((p) => p.isActive) : [];

  const handleSaveOrg = async () => {
    await updateOrganization.mutateAsync({
      name: orgForm.name.trim(),
      nickname: orgForm.nickname.trim(),
      billingEmail: orgForm.billingEmail.trim() || null,
      taxId: orgForm.taxId.trim() || null,
    });
    await refetch();
  };

  const handleCreateSubscription = async () => {
    await createSubscription.mutateAsync({
      organizationUuid: uuid,
      planUuid: subscriptionForm.planUuid,
      status: subscriptionForm.status,
      trialDays: subscriptionForm.status === 'trialing' ? Number(subscriptionForm.trialDays) : undefined,
    });
    await refetch();
  };

  const handleUpdateSubscription = async () => {
    if (!subscriptionUuid) return;
    await updateSubscription.mutateAsync({
      planUuid: subscriptionForm.planUuid || undefined,
      status: subscriptionForm.status,
    });
    await refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !org) {
    return <Alert severity="error">{error?.message || t('common.error')}</Alert>;
  }

  return (
    <Stack spacing={{ xs: 2, sm: 3 }}>
      <Box>
        <Link
          component={RouterLink}
          to="/organizations"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1,
            py: 0.5,
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          {t('organizations.back')}
        </Link>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            wordBreak: 'break-word',
          }}
        >
          {org.name}
        </Typography>
        {org.nickname && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {org.nickname}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ mb: { xs: 1.5, sm: 2 } }}>
          {t('metrics.title')}
        </Typography>
        <PlatformMetricsView
          metrics={orgMetrics}
          isLoading={metricsLoading}
          error={metricsError}
          scope="organization"
        />
      </Box>

      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12} lg={6}>
          <OrganizationDetailsForm
            form={orgForm}
            onChange={setOrgForm}
            onSave={handleSaveOrg}
            isSaving={updateOrganization.isPending}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <OrganizationEntitlementsPanel org={org} />
        </Grid>
        <Grid item xs={12}>
          <OrganizationSubscriptionPanel
            subscription={org.subscription}
            subscriptionForm={subscriptionForm}
            onFormChange={setSubscriptionForm}
            activePlans={activePlans}
            onCreate={handleCreateSubscription}
            onUpdate={handleUpdateSubscription}
            isCreating={createSubscription.isPending}
            isUpdating={updateSubscription.isPending}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
